<template>
  <div
    class="input-label"
    :class="{
      'input-label--icon': !!$slots.icon,
      'input-label--dropdown-visible': !!$slots.dropdown && dropdownVisible
    }"
  >
    <div
      class="input-label__labels"
      v-if="!!$slots.label || !!$slots['label-right']"
    >
      <div class="input-label__labels-item-left" v-if="!!$slots.label">
        <slot name="label" />
      </div>

      <div
        class="input-label__labels-item-right"
        v-if="!!$slots['label-right']"
      >
        <slot name="label-right" />
      </div>
    </div>

    <div class="input-label__input">
      <slot />

      <div v-if="!!$slots.autocomplete" class="input-label__autocomplete">
        <simplebar>
          <slot name="autocomplete" />
        </simplebar>
      </div>

      <div v-if="!!$slots.icon" class="input-label__icon">
        <slot name="icon" />
      </div>

      <div v-if="!!$slots.dropdown" class="input-label__dropdown">
        <slot name="dropdown" />
      </div>
    </div>

    <div class="input-label__notification" v-if="!!$slots.notification">
      <slot name="notification" />
    </div>

    <div
      class="input-label__notification input-label__notification_warning"
      v-if="!!$slots['notification-warning']"
    >
      <slot name="notification-warning" />
    </div>

    <div
      v-if="!!$slots['error']"
      class="input-label__error"
      :class="{ 'input-label__error_static': errorPositionStatic }"
    >
      <slot name="error" />
    </div>

    <div
      v-if="!!$slots['tooltip-error']"
      class="input-label__tooltip-error"
      :class="{ 'input-label__error_static': errorPositionStatic }"
    >
      <slot name="tooltip-error" />
    </div>
  </div>
</template>

<script>
export default {
  name: "InputLabel",
  props: {
    errorPositionStatic: {
      type: Boolean,
      default: false
    },
    dropdownVisible: {
      type: Boolean,
      default: false
    }
  }
};
</script>

<style lang="scss">
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.input-label {
  position: relative;
  display: block;

  &--icon &__input {
    position: relative;

    input {
      padding-left: 40px;
    }
  }

  &--dropdown-visible &__dropdown {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }

  &__labels {
    display: flex;
    justify-content: space-between;
    align-items: center;

    margin-bottom: 8px;
    height: 16px;

    &-item {
      font-size: 12px;

      &-left {
        line-height: 1.33;
        color: #6d7179;

        @include respond-down(md) {
          font-size: 11px;
        }
      }
    }
  }

  &__input {
    position: relative;

    input,
    .input {
      height: 48px;
      border-radius: 2px;
      background-color: #ffffff;
      box-sizing: border-box;
      border: 1px solid #cdced0;

      @include respond-down(md) {
        height: 40px;
        padding-left: 12px;
        padding-right: 12px;
      }

      width: 100%;

      outline: none;

      padding-left: 16px;
      padding-right: 16px;

      &:hover,
      &:focus {
        border-color: #a3a8b3;
      }

      &[disabled],
      &.disabled {
        background: rgba(241, 241, 241, 0.5);
        border-color: rgba(205, 206, 208, 0.5);

        pointer-events: none;
        touch-action: none;

        color: #000000;
      }

      &::placeholder {
        color: #cdced0;
      }

      &[type="search"] {
        border-radius: 0;
        -webkit-appearance: none;
      }

      &.error {
        border-color: #ea5330;
        color: #ea5330;
      }
    }
  }

  &__input .input {
    line-height: 48px;
  }

  &__input .textarea {
    line-height: 120%;
    padding-top: 13px;
    padding-bottom: 13px;
  }

  &__notification {
    padding: 13px 18px;

    border-radius: 1px;
    background: #f0f5f9;

    font-size: 12px;
    line-height: 1.33;

    color: #6d7179;

    position: relative;

    display: flex;
    align-items: center;

    .delete {
      @include button-clean();

      flex-shrink: 0;
      margin-left: auto;
      padding-left: 10px;

      .svg-icon--delete {
        width: 16px;
        height: 16px;
      }
    }

    &::after {
      content: "";

      display: block;

      width: 0;
      height: 0;

      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-bottom: 5px solid #f0f5f9;

      position: absolute;
      top: -5px;
      left: 15px;
    }

    &_warning {
      background-color: #fef4ea;
      color: #f6931d;

      &::after {
        border-bottom-color: #fef4ea;
      }
    }
  }

  &__error {
    position: absolute;
    bottom: -30px;
    left: 0;

    font-size: 12px;
    color: #ea5330;

    //pointer-events: none;
    user-select: none;

    @include respond-down(md) {
      bottom: -14px;
      font-size: 10px;
    }

    @include respond-down(md) {
      position: static;
      white-space: normal;
      margin-top: 8px;
    }

    &_static {
      position: static;
      white-space: normal;
      margin-top: 8px;
    }
  }

  &__tooltip-error {
    display: block;
    position: absolute;
    z-index: 2;
    left: 0;
    top: 100%;

    padding: 18px 18px 13px;
    font-size: 12px;
    line-height: 16px;
    border-radius: 2px;
    color: #ea5330;
    background-color: #fdeeea;

    &::after {
      content: "";

      display: block;

      width: 0;
      height: 0;

      border-left: 10px solid transparent;
      border-right: 10px solid transparent;
      border-bottom: 10px solid #fdeeea;

      position: absolute;
      top: -8px;
      left: 11px;
    }
  }

  &__autocomplete {
    position: absolute;
    z-index: 1;
    top: calc(100% + 10px);
    left: 0;
    overflow: hidden;
    width: 100%;
    border-radius: 2px;
    box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.1);
    background-color: #ffffff;

    .ps {
      max-height: 200px;
    }
  }

  &__icon {
    width: 16px;
    height: 16px;
    position: absolute;
    left: 15px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;

    .svg-icon {
      max-width: 100%;
      max-height: 100%;
    }
  }

  &__dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 10;
    opacity: 0;
    visibility: hidden;
    background: var(--white, #fff);
    box-shadow: 0 40px 60px 0 rgba(0, 0, 0, 0.15);
    transition: all var(--transition, 0.2s);
  }
}
</style>

<style lang="scss">
@import "@v2/app/styles/mixins";

.item-autocomplete {
  @include button-clean();
  box-sizing: border-box;

  display: block;
  width: 100%;
  padding: 10px 16px;

  font-size: 13px;
  line-height: 1.54;
  color: #000;
  text-align: left;

  &:hover {
    background: #009ee3;
    color: #fff;
  }
}
</style>
